import React, { useEffect, useState, useRef } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import Svg from '@styles/icons/svg';
import { font as initialFont } from '@styles/fonts';
import { SmallLoading } from '@components/loading';
import TouchableElement from '@components/common/touchableElement';
import { scale as initialScale } from '@components/frame';
import { isPhone } from '@helpers';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

interface TileProps {
  name: string;
  value?: number;
  elementWidth: number;
  facetsLoaded: boolean;
  icon: { name: string; color: string };
  C: Function;
  api?: (params: any, select: string[]) => Promise<number>;
  params?: Record<string, any>;
  select?: string[];
  level: string;
  useFacet: boolean;
  isSearchTriggered?: any;
  statisticTileName: string;
  setStatisticTileName: (name: string) => void;
  setStatStorage: ({ name, value }: { name: string; value: number }) => void;
  setStatContextStorage: (name: string | null) => void;
  search: (params: any) => Promise<void>;
  oDataFilter?: string;
  statStorageName?: string;
  statContext?: string;
  testID?: string;
}

const Tile: React.FC<TileProps> = ({
  scale = initialScale,
  font = initialFont,
  name,
  value,
  facetsLoaded,
  icon,
  C,
  api,
  params,
  select,
  level,
  useFacet,
  elementWidth,
  isSearchTriggered,
  statisticTileName,
  setStatisticTileName,
  search,
  oDataFilter,
  setStatStorage,
  statStorageName,
  setStatContextStorage,
  statContext,
  testID,
}) => {
  const [amount, setAmount] = useState<number>(0);
  const [loaded, setLoaded] = useState<boolean>(false);

  const { IS_NARROW_WEB } = useDeviceSize();

  useEffect(() => {
    if (!api) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const data = await api(params, select || []);
      if (cancelled) return;
      setAmount(data);
      if (statStorageName) {
        setStatStorage({ value: data, name: statStorageName });
      }
      setLoaded(true);
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [level, isSearchTriggered, params?.oDataFilter, params?.search]);

  const renderAmount = (useFacet ? value : amount) || 0;
  const renderLoaded = useFacet ? facetsLoaded : loaded;

  const isSameName = statisticTileName === name;
  const hasActiveSelection = !!statisticTileName;
  const isFrozen = hasActiveSelection && !isSameName;

  const lastUnfilteredValueRef = useRef<number | null>(null);
  useEffect(() => {
    if (!hasActiveSelection) {
      lastUnfilteredValueRef.current = renderAmount;
    }
  }, [hasActiveSelection, renderAmount]);

  const hasSnapshot = lastUnfilteredValueRef.current !== null;
  const displayedAmount = isFrozen && hasSnapshot ? lastUnfilteredValueRef.current : renderAmount;
  const displayedLoaded = isFrozen && hasSnapshot ? true : renderLoaded;

  const handlePress = () => {
    const currentName = isSameName ? '' : name;
    const currentODataFilter = isSameName ? oDataFilter : params?.oDataFilter;

    setStatisticTileName(currentName);
    search({ level, oDataFilter: currentODataFilter, resetHistory: true, name });
    if (statContext) {
      setStatContextStorage(isSameName ? null : statContext);
    }
  };

  return (
    <TouchableElement testID={testID} onPress={handlePress}>
      {({ boxStyle }) => (
        <View
          style={[
            boxStyle,
            {
              width: elementWidth,
              height: scale(isPhone ? 44 : 56),
              minHeight: scale(isPhone ? 44 : 56),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              borderWidth: isSameName ? 2 : 1,
              borderColor: isSameName ? C('Link/Light/Primary ') : C('Hairline/Light/No trans'),
              paddingHorizontal: 7,
              backgroundColor: C('Background/Light/Field'),
            },
          ]}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: scale(isPhone ? 2 : 4), gap: scale(6) }}>
            <Svg icon={icon.name} width={scale(16)} height={scale(16)} color={icon.color} />
            <View
              style={[
                {
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  height: scale(isPhone ? 20 : 24),
                  alignItems: 'center',
                },
                isPhone && { transform: [{ translateY: !IS_NARROW_WEB ? -2 : -1 }] },
              ]}
            >
              {displayedLoaded ? (
                <Text style={[font[isPhone ? 'Headline' : 'Headline Large'], { color: C('Label/Light/Primary') }]}>{displayedAmount}</Text>
              ) : (
                <View
                  style={{
                    transform: [{ scale: 0.8 }, { translateY: isPhone && !IS_NARROW_WEB ? 2 : IS_NARROW_WEB ? 1 : 0 }],
                    height: scale(isPhone ? 20 : 24),
                    justifyContent: 'center',
                  }}
                >
                  <SmallLoading />
                </View>
              )}
            </View>
          </View>
          <Text
            numberOfLines={1}
            ellipsizeMode="tail"
            style={[
              isPhone ? font['Caption 1'] : font.Body,
              {
                color: isPhone ? C('Label/Light/Secondary') : C('Label/Light/Tertiary'),
              },
            ]}
          >
            {i18n.t(name)}
          </Text>
        </View>
      )}
    </TouchableElement>
  );
};

export default Tile;
